// @flow
import _debug from 'debug';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { show as showSnackbar } from '@justpark/snackbar/src/reducers/snackbar';
import { uniqBy } from 'lodash';
import ApiClient from '../api/Api';
// eslint-disable-next-line import/no-cycle
import type { State as AuthState, User } from './auth';

const debug = _debug('jp:reducers:consents');

const GET_CONSENTS = 'consents/GET_CONSENTS';
const GET_CONSENTS_SUCCESS = 'consents/GET_CONSENTS_SUCCESS';
const GET_CONSENTS_FAIL = 'consents/GET_CONSENTS_FAIL';
const SET_CONSENTS = 'consents/SET_CONSENTS';
const SET_CONSENTS_SUCCESS = 'consents/SET_CONSENTS_SUCCESS';
const SET_CONSENTS_FAIL = 'consents/SET_CONSENTS_FAIL';
const TOGGLE_CONSENT = 'consents/TOGGLE_CURRENT_CONSENT';
const SET_WARNING = 'consents/SET_WARNING';
const SET_MODAL_WARNING = 'consents/SET_MODAL_WARNING';
const REDIRECTING = 'consents/REDIRECTING';

declare var __CLIENT__: boolean;

const MARKETING_CONSENT = 'marketing';

type Consent = {|
  name: string,
  accepted: boolean,
  body: string
|};

export type State = {|
  loaded: boolean,
  loading: boolean,
  consented: Array<Consent>,
  declined: Array<Consent>,
  pending: Array<Consent>,
  consentsSaved: boolean,
  intro: string,
  footer: string,
  warning: string,
  modalWarning: string,
  deleted: boolean,
  revokeError: string,
  redirecting: string,
  deleteAccount: boolean,
  hasActiveOrUpcomingBookings: boolean,
  balanceNotZero: boolean,
  isLoadingDeleteCriteria: boolean,
  fetchDeleteCriteriaError: string,
  hasFetchedDeleteCriteria: boolean
|};

export type Action =
  | {| type: typeof GET_CONSENTS |}
  | {| type: typeof GET_CONSENTS_SUCCESS, payload: any, queryParams: any |}
  | {| type: typeof GET_CONSENTS_FAIL |}
  | {| type: typeof SET_CONSENTS |}
  | {| type: typeof SET_CONSENTS_SUCCESS |}
  | {| type: typeof SET_CONSENTS_FAIL |}
  | {| type: typeof TOGGLE_CONSENT, payload: string |}
  | {| type: typeof SET_WARNING, payload: string |}
  | {| type: typeof SET_MODAL_WARNING, payload: string |}
  | {| type: typeof REDIRECTING, payload: string |};

export type ClientAction = {|
  types: Array<string>,
  params?: any,
  promise: (ApiClient) => Promise<{}>
|};

export const initialState: State = {
  loaded: false,
  loading: false,
  consentsSaved: false,
  consented: [],
  declined: [],
  pending: [],
  intro: '',
  footer: '',
  warning: '',
  modalWarning: '',
  deleted: false,
  revokeError: '',
  redirecting: '',
  deleteAccount: false,
  hasActiveOrUpcomingBookings: false,
  balanceNotZero: false,
  isLoadingDeleteCriteria: true,
  fetchDeleteCriteriaError: '',
  hasFetchedDeleteCriteria: false
};
export const getDeleteCriteria = createAsyncThunk(
  'consents/getDeleteCriteria',
  async (payload, { extra: { client }, rejectWithValue }) => {
    try {
      const { data } = await client.deleteCriteria();
      return data;
    } catch (e) {
      return rejectWithValue({
        message: e?.message || e,
        code: e?.error || 0
      });
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'consents/deleteAccount',
  async (
    { queryParams, navigate, isWebview },
    { dispatch, extra: { client, t }, rejectWithValue }
  ) => {
    const next = queryParams?.params?.next;
    try {
      const response = await client.consents.deleteAccount();
      dispatch(
        showSnackbar(
          t(
            'deleteAccount:snackbarMessage',
            'You have successfully deleted your account!'
          )
        )
      );
      if (!isWebview) {
        navigate('/');
      } else {
        window.location = next || `${window.location.origin}/`;
      }
      return response;
    } catch (e) {
      return rejectWithValue({
        message: e?.message || e,
        code: e?.error || 0
      });
    }
  }
);

const consentsSlice = createSlice({
  name: 'consents',
  initialState,
  reducers: {
    toggleDeleteAccount: (state: State, action: PayloadAction) => ({
      ...state,
      deleteAccount: action.payload
    }),
    resetDeleteCriteria: (state: State): State => ({
      ...state,
      hasFetchedDeleteCriteria: false,
      balanceNotZero: false,
      hasActiveOrUpcomingBookings: false,
      isLoadingDeleteCriteria: true
    })
  },
  extraReducers: {
    [REDIRECTING](state: State, action: Action): State {
      debug(REDIRECTING);
      return {
        ...state,
        redirecting: action.payload
      };
    },
    [SET_MODAL_WARNING](state: State, action: Action): State {
      debug(SET_MODAL_WARNING);
      return {
        ...state,
        modalWarning: action.payload
      };
    },
    [SET_WARNING](state: State, action: Action): State {
      debug(SET_WARNING);
      return {
        ...state,
        warning: action.payload
      };
    },
    [GET_CONSENTS](state: State): State {
      debug(GET_CONSENTS);
      return {
        ...state,
        loading: true,
        loaded: false,
        consented: [],
        declined: [],
        pending: []
      };
    },
    [GET_CONSENTS_SUCCESS](state: State, action: Action): State {
      debug(GET_CONSENTS_SUCCESS);
      const { type, consent: queryConsent } = action.queryParams;

      const paramConsent = queryConsent || [];

      let requested =
        typeof paramConsent === 'string' ? [paramConsent] : paramConsent;

      const consents = action.payload.data;
      const update: State = {
        ...state,
        loading: false,
        loaded: true,
        consentsSaved: state.consented.length !== 0,
        intro: consents.intro.data[0].body,
        footer: consents.footer.data[0].body,
        pending: consents.pending.data,
        declined: consents.declined.data,
        consented: consents.consented.data.map((consent) => ({
          ...consent,
          accepted: true
        }))
      };

      if (type === 'add-listing') {
        requested = requested.concat(['space_owner']);
      }

      if (requested.length > 0) {
        return {
          ...update,
          pending: consents.pending.data.filter(({ name }) =>
            requested.includes(name)
          ),
          consented: [],
          declined: []
        };
      }

      return (update: State);
    },
    [GET_CONSENTS_FAIL](state: State, action: Action): State {
      debug(GET_CONSENTS_FAIL, action);
      return { ...state, loaded: false, loading: false };
    },
    [SET_CONSENTS](state: State): State {
      debug(SET_CONSENTS);
      return {
        ...state,
        loading: true,
        loaded: false
      };
    },
    [SET_CONSENTS_SUCCESS](state: State): State {
      debug(SET_CONSENTS_SUCCESS);
      return {
        ...state,
        consentsSaved: true,
        loading: false
      };
    },
    [SET_CONSENTS_FAIL](state: State, action: Action): State {
      debug(SET_CONSENTS_FAIL, action);
      return { ...state, loading: false };
    },
    [deleteAccount.pending](state: State): State {
      debug(deleteAccount.pending);
      return { ...state, loading: true };
    },
    [deleteAccount.fulfilled](): State {
      debug(deleteAccount.fulfilled);
      return {
        ...initialState,
        deleted: true,
        loading: false
      };
    },
    [deleteAccount.rejected](state: State, action: Action): State {
      debug(deleteAccount.rejected, action);
      return {
        ...state,
        loading: false,
        revokeError: action.payload ? action.payload.meta.user : ''
      };
    },
    [TOGGLE_CONSENT](state: State, action: Action): State {
      debug(TOGGLE_CONSENT, action);
      const n = action.payload;
      const pending = state.pending.filter(({ name }) => name === n)[0];
      const declinedC = state.declined.filter(({ name }) => name === n)[0];
      const consented = state.consented.filter(({ name }) => name === n)[0];
      if (pending) {
        return {
          ...state,
          pending: state.pending.filter(({ name }) => name !== n),
          consented: state.consented.concat([
            {
              ...pending,
              accepted: true
            }
          ])
        };
      }
      if (declinedC) {
        const { accepted, ...consent } = declinedC;
        return {
          ...state,
          declined: state.declined.filter(({ name }) => name !== n),
          consented: state.consented.concat([
            {
              ...consent,
              accepted: true
            }
          ])
        };
      }
      const { accepted, ...consent } = consented;
      return {
        ...state,
        consented: state.consented.filter(({ name }) => name !== n),
        declined: state.declined.concat([
          {
            ...consent,
            accepted: false
          }
        ])
      };
    },
    [getDeleteCriteria.pending]: (state: State): State => ({
      ...state,
      isLoadingDeleteCriteria: true,
      fetchDeleteCriteriaError: ''
    }),
    [getDeleteCriteria.fulfilled]: (
      state: State,
      action: PayloadAction
    ): State => ({
      ...state,
      ...action.payload,
      isLoadingDeleteCriteria: false,
      hasFetchedDeleteCriteria: true
    }),
    [getDeleteCriteria.rejected]: (
      state: State,
      action: PayloadAction
    ): State => ({
      ...state,
      ...action.payload,
      isLoadingDeleteCriteria: false,
      fetchDeleteCriteriaError: action.payload.message,
      hasFetchedDeleteCriteria: true
    })
  }
});

/**
 * Load consents from the API
 * The getConsents action below also does funky redirect and other stuff we may not want
 * @returns {function(*): *}
 */
export const loadConsents = () => (dispatch) =>
  dispatch({
    types: [GET_CONSENTS, GET_CONSENTS_SUCCESS, GET_CONSENTS_FAIL],
    queryParams: {},
    promise: (client) => client.consents.get()
  });

export function getConsents(
  queryParams: any = {},
  refresh: boolean,
  pathname?: string = '',
  user?: User
): (
  (ClientAction) => *,
  () => { consents: State, auth: AuthState }
) => Promise<any> {
  return (dispatch, getStore) => {
    const state = getStore().consents;
    const { isSpaceOwner } = getStore().auth.user || user || {};
    const { consent, type } = queryParams;
    const shouldFilterListing =
      !consent && !type && pathname && pathname.includes('dashboard');
    const filterListing = (response) =>
      !shouldFilterListing
        ? response
        : {
            data: {
              ...response.data,
              pending: {
                data:
                  isSpaceOwner && !getStore().auth.newUser
                    ? response.data.pending.data
                    : response.data.pending.data.filter(
                        ({ name }) => name !== 'space_owner'
                      )
              }
            }
          };
    if ((!state.loading && !state.loaded) || refresh) {
      return dispatch({
        types: [GET_CONSENTS, GET_CONSENTS_SUCCESS, GET_CONSENTS_FAIL],
        queryParams,
        promise: (client) =>
          client.consents.get().then((response) => {
            const result = filterListing(response);
            return result;
          })
      });
    }
    const response = filterListing({
      data: {
        intro: { data: [{ name: 'intro', body: state.intro }] },
        pending: { data: state.pending },
        declined: { data: state.declined },
        consented: { data: state.consented },
        footer: { data: [{ name: 'footer', body: state.footer }] }
      }
    });
    return Promise.resolve(response);
  };
}

export function revokeSpaceOwner() {
  return (dispatch: (*) => *, getState: () => { consents: State }) => {
    const data = getState()
      .consents.pending.filter(({ name }: Consent) => name === 'space_owner')
      .map((consent: Consent) => ({ accepted: false, ...consent }));
    return dispatch({
      types: [SET_CONSENTS, SET_CONSENTS_SUCCESS, SET_CONSENTS_FAIL],
      promise: (client: typeof ApiClient) =>
        client.consents.create(data).then(() => dispatch(getConsents({}, true)))
    });
  };
}

export function setMarketingConsent(accepted: boolean) {
  return (dispatch: (*) => *, getState: () => { consents: State }) => {
    const data = getState()
      .consents.pending.filter(
        ({ name }: Consent) => name === MARKETING_CONSENT
      )
      .map((consent: Consent) => ({ accepted, ...consent }));

    return dispatch({
      types: [SET_CONSENTS, SET_CONSENTS_SUCCESS, SET_CONSENTS_FAIL],
      promise: (client: typeof ApiClient) =>
        client.consents.create(data).then(() => dispatch(getConsents({}, true)))
    });
  };
}

export function setConsents(force: boolean, pathname, queryParams?: any) {
  return (
    dispatch: (*) => *,
    getState: () => { consents: State, auth: AuthState }
  ) => {
    const pending = getState().consents.pending.map(({ name }) => name);
    const consented = getState().consents.consented.map(({ name }) => name);
    const declined = getState().consents.declined.map(({ name }) => name);
    if (
      !force &&
      (pending.includes('space_owner') || declined.includes('space_owner')) &&
      consented.includes('standard') &&
      (getState().auth.user || {}).isSpaceOwner
    ) {
      return dispatch({
        type: SET_MODAL_WARNING,
        payload:
          'You will lose any listing data you have saved unless you consent to keeping it.'
      });
    }
    if (!force && !consented.includes('standard')) {
      return dispatch({
        type: SET_WARNING,
        payload:
          "You must either tick the 'Standard' checkbox or 'I disagree with all of the above...' link below"
      });
    }
    const data = uniqBy(
      getState()
        .consents.consented.concat(
          getState().consents.declined.map(({ accepted, ...consent }) => ({
            ...consent,
            accepted: false
          }))
        )
        .concat(
          pathname && pathname.includes('dashboard')
            ? getState().consents.pending.map(({ accepted, ...consent }) => ({
                ...consent,
                accepted: false
              }))
            : []
        ),
      ({ name }) => name
    );
    return dispatch({
      types: [SET_CONSENTS, SET_CONSENTS_SUCCESS, SET_CONSENTS_FAIL],
      promise: (client: typeof ApiClient) =>
        client.consents.create(data).then(() => {
          if (pathname && pathname.includes('dashboard') && __CLIENT__) {
            window.location = `${window.location.origin}/dashboard`;
          } else {
            dispatch(
              getConsents(
                { ...queryParams }, // TODO: needs a destruct as there is a bug with encoded next urls
                true,
                pathname,
                getState().auth.user || undefined
              )
            );

            dispatch(showSnackbar('Preferences updated'));
          }
        })
    });
  };
}

export function toggleConsent(name: string): Action {
  return {
    type: TOGGLE_CONSENT,
    payload: name
  };
}

export function cancelWarning(): Action {
  return {
    type: SET_MODAL_WARNING,
    payload: ''
  };
}

export const analyticsEventSubscribers = {
  [SET_CONSENTS_SUCCESS]: (
    action: Action,
    analytics: any,
    getState: () => any
  ) => {
    const state = getState();
    analytics.track('consents:success', {
      newUser: state.auth.newUser,
      marketing:
        state.consents.consented
          .map((consent) => consent.name === MARKETING_CONSENT)
          .filter((b) => b).length > 0
    });
  },
  [SET_CONSENTS_FAIL]: (action: Action, analytics: any) => {
    analytics.track('consents:fail', action);
  }
};

/**
 * Do we have a logged in user without loaded consents
 */
export const selectConsentsCanBeLoaded = (state) => {
  const isLoggedIn = state.auth.user?.id !== undefined;
  const consentsLoaded = state.consents.loaded;
  const consentsLoading = state.consents.loading;

  return isLoggedIn && !consentsLoaded && !consentsLoading;
};

export const selectConsentsHasLoaded = (state: { consents: State }) =>
  state.consents.loaded;

export const selectConsentsShouldAskForMarketingConsent = (state: {
  consents: State
}) =>
  // Declined consents appear in both pending and declined.
  // Check marketing is pending and not declined
  state.consents.pending.find(
    (item: Consent) => item.name === MARKETING_CONSENT
  ) !== undefined &&
  state.consents.declined.find(
    (item: Consent) => item.name === MARKETING_CONSENT
  ) === undefined;

export const selectConsentsHasOptedInToMarketingConsent = (state: {
  consents: State
}) =>
  state.consents.consented
    .map((consent) => consent.name === MARKETING_CONSENT)
    .filter((b) => b).length > 0;

export const { toggleDeleteAccount } = consentsSlice.actions;
export const selectConsentsDeleteAccount = (state: { consents: State }) =>
  state.consents.deleteAccount;
export const selectConsentsDeleteAccountLoading = (state: {
  consents: State
}) => state.consents.loading;
export const selectConsentsHasActiveOrUpcomingBookings = (state: {
  consents: State
}) => state.consents.hasActiveOrUpcomingBookings;
export const selectConsentsHasPendingWithdrawals = (state: {
  consents: State
}) => state.consents.balanceNotZero;
export const selectConsentsHasFetchedDeleteCriteria = (state: {
  consents: State
}) => state.consents.hasFetchedDeleteCriteria;
export const selectConsentsIsLoadingDeleteCriteria = (state: {
  consents: State
}) => state.consents.isLoadingDeleteCriteria;

export default consentsSlice;

export const { resetDeleteCriteria } = consentsSlice.actions;
